import cx from 'classnames'
import { useContext } from 'react'

import {
  type SanityBlogPostWithoutBody,
  type SanityBlogPostOptions,
} from '@data/sanity/queries/types/blog'
import { getFormattedDate } from '@lib/format'
import { LanguageContext } from '@lib/language-context'
import { getLinkPageUrl } from '@lib/routes'
import { StringsContext } from '@lib/strings-context'

import Photo from '@components/photo'
import SimpleLink from '@components/simple-link'
import Icon from '@components/icon'

type VerticalAlign = 'top' | 'center'

interface BlogPostListItemProps {
  post: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
  verticalAlign?: VerticalAlign
}

const BlogPostListItem = ({
  post,
  options,
  verticalAlign,
}: BlogPostListItemProps) => {
  const strings = useContext(StringsContext)
  const { locale } = useContext(LanguageContext)

  return (
    <div
      className={cx('grid grid-cols-1 lg:grid-cols-2', {
        'items-start': !verticalAlign || verticalAlign === 'top',
        'items-center': verticalAlign === 'center',
      })}
    >
      {!!post.coverImage && !!options?.showCoverImage && (
        <Photo
          className="mb-5"
          image={{
            ...post.coverImage,
            customRatio: 1.7777777778,
            borderRadius: 'lg',
          }}
        />
      )}
      <div className="lg:px-10 xl:px-28 lg:py-8 flex flex-col gap-1 justify-center">
        <h3 className="mb-4">{post.title}</h3>
        {!!options?.showExcerpt && <p className="mb-6">{post.excerpt}</p>}
        {!!options?.showCategories && (
          <div className="flex flex-wrap mb-4">
            {post.categories?.map((category) => (
              <SimpleLink
                key={`${post.slug.current}-category-${category.slug.current}`}
                href={getLinkPageUrl('blogCategory', category.slug.current)}
                className="mr-2 text-[12px] opacity-50"
              >
                {category.name}
              </SimpleLink>
            ))}
          </div>
        )}
        {(!!options?.showAuthorPhoto ||
          !!options?.showAuthorName ||
          !!options?.showDate) && (
          <div className="flex items-center mb-5">
            {options?.showAuthorPhoto && post.author?.photo && (
              <Photo
                image={post.author?.photo}
                width={40}
                height={40}
                className="rounded-full overflow-hidden shrink-0 w-[40px] h-[40px] mr-2"
                imageClassName="object-cover"
              />
            )}
            <div>
              {options?.showAuthorName && (
                <p className="text-sm">
                  {strings.blogPostBy}{' '}
                  <SimpleLink
                    href={getLinkPageUrl(
                      'blogAuthor',
                      post.author?.slug?.current,
                    )}
                  >
                    {post.author?.name}
                  </SimpleLink>
                  {options?.showDate
                    ? `, ${getFormattedDate(
                        post.date ?? post._createdAt,
                        locale,
                      )}`
                    : ''}
                </p>
              )}
            </div>
          </div>
        )}

        <SimpleLink
          href={getLinkPageUrl('blogPost', post.slug.current)}
          className="uppercase inline-flex items-center font-medium text-sm"
        >
          <Icon
            id="read-more-icon"
            name="ArrowRight"
            className="mr-2 text-2xl"
          />
          {strings.blogPostReadMore}
        </SimpleLink>
      </div>
    </div>
  )
}

export default BlogPostListItem
